import { useState, useEffect } from 'react';
import { Loader2, BookOpen } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import CloseReadingExercise from './CloseReadingExercise';

interface Category {
  id: string;
  group_name: string;
  name: string;
  label: string;
  description: string | null;
  color: string | null;
}

interface Exercise {
  id: string;
  title: string;
  aiText: string;
  aiModel: string | null;
  aiPrompt: string | null;
  contextNotes: string | null;
  minAnnotations: number;
  difficulty: string;
}

interface Submission {
  id: string;
  annotations: Array<{ start: number; end: number; categories: string[]; note?: string }>;
  claims: string | null;
  argument: string | null;
  theory: string | null;
  completedAt: string | null;
}

interface LessonCloseReadingProps {
  lessonId: string;
  lessonMode?: 'NO_TOOL' | 'AUDIT' | 'OPEN_AI';
}

export function LessonCloseReading({ lessonId, lessonMode = 'AUDIT' }: LessonCloseReadingProps) {
  const { fetchApi } = useApi();
  const [loading, setLoading] = useState(true);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [categories, setCategories] = useState<Record<string, Category[]>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExercise();
  }, [lessonId]);

  const loadExercise = async () => {
    try {
      setLoading(true);
      const response = await fetchApi<{
        exercise: Exercise | null;
        submission: Submission | null;
        categories?: Category[];
      }>(`/api/close-reading/exercises/${lessonId}`);

      if (response.exercise) {
        // Also fetch categories if not included
        const catResponse = await fetchApi<{ categories: Record<string, Category[]> }>(
          '/api/close-reading/categories'
        );

        setExercise({
          ...response.exercise,
          // Map the API response to component expected format
          ai_text: response.exercise.aiText,
          ai_model: response.exercise.aiModel,
          ai_prompt: response.exercise.aiPrompt,
          context_notes: response.exercise.contextNotes,
          min_annotations: response.exercise.minAnnotations
        } as unknown as Exercise);
        setSubmission(response.submission ? {
          ...response.submission,
          completed_at: response.submission.completedAt
        } as unknown as Submission : null);
        setCategories(catResponse.categories);
      }
    } catch (err) {
      console.error('Failed to load close reading exercise:', err);
      setError('Failed to load exercise');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-400">
        {error}
      </div>
    );
  }

  if (!exercise) {
    // No close reading exercise for this lesson
    return null;
  }

  // Transform to match CloseReadingExercise expected props
  const exerciseForComponent = {
    id: exercise.id,
    title: exercise.title,
    ai_text: exercise.aiText,
    ai_model: exercise.aiModel,
    ai_prompt: exercise.aiPrompt,
    context_notes: exercise.contextNotes,
    min_annotations: exercise.minAnnotations,
    difficulty: exercise.difficulty
  };

  const submissionForComponent = submission ? {
    id: submission.id,
    annotations: submission.annotations,
    claims: submission.claims,
    argument: submission.argument,
    theory: submission.theory,
    completed_at: submission.completedAt
  } : undefined;

  return (
    <div className="bg-background-secondary rounded-xl p-6 border border-gray-800 mb-8">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-blue-400" />
        <h3 className="text-lg font-semibold text-white">Close Reading Exercise</h3>
      </div>
      <CloseReadingExercise
        exercise={exerciseForComponent}
        submission={submissionForComponent}
        categories={categories}
        mode={lessonMode}
      />
    </div>
  );
}

export default LessonCloseReading;
